// EAP completa da obra (ponto 2 do pedido de stg): navega o orçamento por nível da
// árvore (unidade/célula/etapa/subetapa/serviço), mostrando o previsto de cada item e
// permitindo definir ou editar a regra de desembolso a partir daqui. Mesma rota
// `GET .../disbursement/targets` do `ResourceCatalog`, sem `page`/`pageSize` (a rota
// só aceita catálogo paginado para `level: "resource"` — ver `getDisbursementTargets.js`),
// então a lista vem inteira até `limit`, ordenada pelo previsto (maior primeiro).
import { useEffect, useState } from "react";
import { fetchDisbursementTargets } from "./disbursementApi";
import { NODE_LEVELS } from "./RuleTargetPicker";
import { money } from "./format";

const LIMIT = 200;

const reasonText = (reason) => {
  if (reason === "composition_missing") return "Composição da EAP indisponível.";
  if (reason === "planned_series_missing") return "Previsto da obra indisponível.";
  return "A EAP está parcial ou indisponível no momento.";
};

const inheritedLabel = (source) =>
  typeof source === "string" ? source : source?.label ?? source?.key;

const actionLabel = (item) => {
  if (item.ruleId) return "Editar regra";
  return item.inheritedFrom ? "Sobrescrever aqui" : "Definir";
};

const EapCatalog = ({
  workId,
  readOnly = false,
  onDefineRule,
  reloadKey,
  rulesByRuleId = {},
}) => {
  const [level, setLevel] = useState("service");
  const [search, setSearch] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    fetchDisbursementTargets(workId, { level, search: committedSearch, limit: LIMIT })
      .then((payload) => {
        if (!active) return;
        if (!payload || !Array.isArray(payload.items)) {
          setError(true);
          return;
        }
        setItems(payload.items);
        setReason(payload.reason ?? null);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [workId, level, committedSearch, reloadKey]);

  return (
    <section aria-label="EAP do empreendimento">
      <h3>EAP do empreendimento</h3>
      <p>
        Estrutura orçamentária completa da obra. Escolha o nível da EAP e, se
        precisar, busque um item pelo código ou descrição.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setCommittedSearch(search.trim());
        }}
      >
        <label>
          Nível da EAP
          <select
            aria-label="Nível da EAP"
            value={level}
            onChange={(event) => setLevel(event.target.value)}
          >
            {NODE_LEVELS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Buscar item da EAP
          <input
            aria-label="Buscar item da EAP"
            maxLength={80}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <button type="submit" disabled={loading}>
          Buscar
        </button>
      </form>
      {loading ? <p role="status">Carregando a EAP…</p> : null}
      {error ? (
        <p role="alert">
          Não foi possível carregar a EAP. Tente novamente.
        </p>
      ) : null}
      {!loading && !error && reason ? (
        <p role="status">{reasonText(reason)}</p>
      ) : null}
      {!loading && !error ? (
        <p>{`${items.length.toLocaleString("pt-BR")} ${
          items.length === 1 ? "item encontrado" : "itens encontrados"
        }${items.length === LIMIT ? " (mostrando os de maior previsto)" : ""}`}</p>
      ) : null}
      {!loading && !error && items.length === 0 ? (
        <p>Nenhum item da EAP encontrado neste nível ou busca.</p>
      ) : null}
      {!loading && !error && items.length > 0 ? (
        <ul className="ampli-disbursement-list__rows">
          {items.map((item) => (
            <li key={item.key} className="ampli-rule-row">
              <div className="ampli-rule-row__identity">
                <strong>{item.label}</strong>
                <span>
                  {item.ruleId ? "Regra própria cadastrada" : "Sem regra própria"}
                </span>
                {item.inheritedFrom ? (
                  <span>{`Regra herdada: ${inheritedLabel(item.inheritedFrom)}`}</span>
                ) : null}
              </div>
              <div className="ampli-rule-row__facts">
                <span>{money(item.plannedCents)}</span>
              </div>
              {!readOnly ? (
                <button
                  type="button"
                  disabled={Boolean(item.ruleId && !rulesByRuleId[item.ruleId])}
                  onClick={() => onDefineRule?.(item)}
                >
                  {actionLabel(item)}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default EapCatalog;
